import { useMemo } from 'react'
import type { Demanda, User, DemandaAtribuida, ObservadorData } from '@/models'

// Função utilitária para normalizar dados de observadores (replicada do DemandDetails)
const normalizeObservadorData = (
  observadorData: ObservadorData
): { user: User; observador_fk: number } => {
  // Se já é um objeto Observador com propriedade observador
  if ('observador' in observadorData && observadorData.observador) {
    return {
      user: observadorData.observador,
      observador_fk: observadorData.observador_fk,
    }
  }

  // Se é um User com pivot (formato do backend atual)
  if ('name' in observadorData && 'email' in observadorData) {
    const user = observadorData as User & { pivot?: { demanda_fk: number; observador_fk: number } }
    return {
      user: user,
      observador_fk: user.pivot?.observador_fk || user.id,
    }
  }

  // Fallback
  throw new Error('Formato de observador inválido')
}

interface DemandPermissionsResult {
  canPull: boolean
  canAssign: boolean
  canSolve: boolean
  canFinalize: boolean
  canReturn: boolean
  canAccept: boolean
  canReject: boolean
  canComment: boolean
  canAttach: boolean
  canAddObservers: boolean
  canDelete: boolean
  canMarkWrong: boolean
  canResend: boolean
  canUndoDelivery: boolean
  canEdit: boolean
  canEditTemplateFields: boolean // Editar campos editáveis do template (ex: data_pagamento)
  canRemoveAssigned: boolean

  // Informações de contexto
  isCreator: boolean
  isDemandante: boolean
  isAssigned: boolean
  isObserver: boolean
  hasActiveAssignment: boolean
  isGestorDoSetor: boolean
  isAdmin: boolean
}

export const useDemandPermissions = (
  demanda: Demanda | null,
  currentUser: User | null
): DemandPermissionsResult => {
  /*
   * REGRAS DE PERMISSÃO BASEADAS NA ESTRUTURA DO BACKEND:
   *
   * ESTRUTURA:
   * - users.funcao_usuario: ['USUARIO', 'DIRETOR', 'ADMIN'] (função global)
   * - usuarios_setores.funcao: ['USUARIO', 'GESTOR'] (função no setor)
   *
   * REGRAS PARA ATRIBUIÇÃO (ATUALIZADO CONFORME BACKEND):
   *
   * 1. ADMIN (funcao_usuario = 'ADMIN'):
   *    - Pode atribuir sempre se: é criador OU demandante OU pertence ao setor
   *
   * 2. GESTOR (funcao = 'GESTOR' na tabela usuarios_setores):
   *    - Pode atribuir sempre se: é criador OU demandante OU pertence ao setor
   *    - IMPORTANTE: Só é identificado como gestor se a relação setores estiver carregada
   *
   * 3. USUÁRIO COMUM (funcao = 'USUARIO' na usuarios_setores):
   *    - Pode atribuir APENAS quando:
   *      ✓ É responsável atual pela demanda (já puxou/foi atribuído) E
   *      ✓ Pertence ao setor da demanda
   *
   * REGRAS PARA COMENTÁRIOS:
   * - Criador, demandante, responsável atual, observador, gestor ou usuário do setor podem comentar
   * - Isso permite que qualquer membro do setor possa acompanhar e colaborar com as demandas
   *
   * REGRAS PARA REENVIO:
   * - Apenas criador e demandante podem reenviar (backend corrigido)
   *
   * 4. ADMIN (funcao_usuario = 'ADMIN'):
   *    - Pode fazer tudo
   */
  return useMemo(() => {
    // Valores padrão se não houver demanda ou usuário
    if (!demanda || !currentUser) {
      return {
        canPull: false,
        canAssign: false,
        canSolve: false,
        canFinalize: false,
        canReturn: false,
        canAccept: false,
        canReject: false,
        canComment: false,
        canAttach: false,
        canAddObservers: false,
        canDelete: false,
        canMarkWrong: false,
        canResend: false,
        canUndoDelivery: false,
        canEdit: false,
        canEditTemplateFields: false,
        canRemoveAssigned: false,
        isCreator: false,
        isDemandante: false,
        isAssigned: false,
        isObserver: false,
        hasActiveAssignment: false,
        isGestorDoSetor: false,
        isAdmin: false,
      }
    }

    // Informações básicas sobre o usuário em relação à demanda
    const isCreator = demanda.criado_por_fk === currentUser.id
    const isDemandante = demanda.demandante_fk === currentUser.id

    // Verifica se é ADMIN ou DIRETOR (funções globais vindas da coluna funcao_usuario)
    const isAdmin = currentUser.funcao_usuario === 'ADMIN'
    const isDirector = currentUser.funcao_usuario === 'DIRETOR'
    const hasGlobalManagementRole = isAdmin || isDirector

    // Verifica se pertence ao setor da demanda
    // 1) Preferencialmente pelo setor logado
    // 2) Fallback: pela lista de setores do usuário (pivot.active === true)
    // NORMALIZAÇÃO: Converte 0/1 para false/true usando Boolean()
    const belongsToSector =
      currentUser.setor_logado_fk === demanda.setor_fk ||
      (currentUser.setores?.some(s => s.id === demanda.setor_fk && Boolean(s.pivot?.active)) ??
        false)

    // Verifica se é GESTOR do setor da demanda
    // Primeira verificação: se tem a relação setores carregada
    // NORMALIZAÇÃO: Converte 0/1 para false/true usando Boolean()
    const isGestorDoSetor =
      currentUser.setores?.some(
        setor =>
          setor.id === demanda.setor_fk &&
          setor.pivot?.funcao === 'GESTOR' &&
          Boolean(setor.pivot?.active)
      ) || false



    // Considera gestor apenas se tem a relação setores carregada e é GESTOR
    const isGestor = isGestorDoSetor

    // Verifica se o usuário está atribuído (com atribuição ativa)
    // NORMALIZAÇÃO: Converte 0/1 para false/true usando Boolean()
    const activeAssignment = demanda.atribuidas?.find(
      (attr: DemandaAtribuida) =>
        attr.usuario_fk === currentUser.id && Boolean(attr.active) && attr.status !== 'RECUSADA'
    )
    const isAssigned = !!activeAssignment
    const hasActiveAssignment =
      demanda.atribuidas?.some(
        (attr: DemandaAtribuida) => Boolean(attr.active) && attr.status !== 'RECUSADA'
      ) || false

    // Verifica se é observador
    const isObserver =
      demanda.observadores?.some(obs => {
        try {
          const { observador_fk } = normalizeObservadorData(obs)
          return observador_fk === currentUser.id
        } catch {
          // Se é User direto, usar o ID do usuário
          if ('id' in obs && obs.id === currentUser.id) {
            return true
          }
          return false
        }
      }) || false

    // Regras de permissões baseadas no backend

    // PUXAR: Pertence ao setor E demanda não tem responsável ativo E status apropriado
    const canPull =
      belongsToSector &&
      !hasActiveAssignment &&
      ['EMATRIBUICAO', 'RECEBIDA'].includes(demanda.status || '')

    // ATRIBUIR: Regras específicas baseadas na função do usuário
    //
    // 1. ADMIN (funcao_usuario = 'ADMIN'):
    //    - Pode atribuir sempre (criador, demandante ou pertencente ao setor)
    //
    // 2. GESTOR (funcao = 'GESTOR' na tabela usuarios_setores):
    //    - Pode atribuir sempre (criador, demandante ou pertencente ao setor)
    //    - IMPORTANTE: Só é identificado como gestor se a relação setores estiver carregada
    //
    // 3. USUÁRIO COMUM (funcao = 'USUARIO' na usuarios_setores):
    //    - Pode atribuir APENAS quando:
    //      ✓ É responsável pela demanda (já puxou/foi atribuído e aceitou) E
    //      ✓ Pertence ao setor da demanda E
    //      ✓ Status apropriado
    const canAssign = (() => {
      if (!['EMATRIBUICAO', 'RECEBIDA', 'EMPROGRESSO'].includes(demanda.status || '')) {
        return false
      }

      // Admin pode atribuir (no contexto do setor, criador ou demandante)
      if (hasGlobalManagementRole) {
        const result = isCreator || isDemandante || belongsToSector
        return result
      }

      // Gestor pode atribuir (no contexto do setor, criador ou demandante)
      if (isGestor) {
        const result = isCreator || isDemandante || belongsToSector
        return result
      }

      // Usuário comum: somente se já for responsável e tiver ACEITO (em progresso)
      const result = isAssigned && activeAssignment?.status === 'EMPROGRESSO' && belongsToSector
      return result
    })()

    // SOLUCIONAR: Está atribuído E status da atribuição é EMPROGRESSO
    const canSolve = isAssigned && activeAssignment?.status === 'EMPROGRESSO'

    // ACEITAR: Está atribuído E status da atribuição é RECEBIDA
    const canAccept = isAssigned && activeAssignment?.status === 'RECEBIDA'

    // FINALIZAR: É demandante OU criador E status solucionada
    const canFinalize = (isDemandante || isCreator) && demanda.status === 'SOLUCIONADA'

    // DEVOLVER: É demandante OU criador E status solucionada
    const canReturn = (isDemandante || isCreator) && demanda.status === 'SOLUCIONADA'

    // RECUSAR: Está atribuído E status da atribuição é RECEBIDA
    const canReject = isAssigned && activeAssignment?.status === 'RECEBIDA'

    // DESFAZER ENTREGA: Quem solucionou pode desfazer se ainda está em solucionada
    const canUndoDelivery = isAssigned && demanda.status === 'SOLUCIONADA'

    // COMENTAR: Criador OU demandante OU atribuído OU observador OU admin OU pertence ao setor
    // Alinhado com o backend
    // Observador pode comentar mesmo que não tenha mais atribuição ativa
    // Usuários do setor podem comentar para acompanhar e colaborar
    const canComment = (() => {
      // Alinhado com o backend:
      const podeComentar = 
        isCreator || 
        isDemandante || 
        isAssigned || 
        isObserver || 
        isAdmin || 
        isGestor ||
        belongsToSector // ← Adicionado: usuários do setor podem comentar

      return podeComentar
    })()

    // ANEXAR: Mesma regra do comentar
    const canAttach = canComment

    // ADICIONAR OBSERVADORES: Criador, demandante ou responsável atual
    const canAddObservers = isCreator || isGestor

    // EXCLUIR: Apenas criador e status específicos
    const canDelete =
      isCreator && ['EMATRIBUICAO', 'RECEBIDA', 'ERRADA'].includes(demanda.status || '')

    // MARCAR COMO ERRADA: Apenas gestor/admin OU responsável atual
    const canMarkWrong =
      (isGestor || isAdmin || isAssigned) &&
      !['FINALIZADA', 'EXCLUIDA', 'CANCELADA'].includes(demanda.status || '')

    // REENVIAR/REMANDAR: Apenas criador e demandante E somente quando a demanda estiver ERRADA
    const canResend = (isCreator || isDemandante) && demanda.status === 'ERRADA'

    // EDITAR: Criador E status apropriado
    const canEdit =
      isCreator && ['EMATRIBUICAO', 'RECEBIDA', 'ERRADA'].includes(demanda.status || '')

    // EDITAR CAMPOS DO TEMPLATE: Permite edição inline de campos editáveis do template
    // Disponível para: criador em status editáveis OU atribuído em EMPROGRESSO
    // Isso permite que o setor responsável informe a data real do pagamento
    const canEditTemplateFields =
      canEdit || // Criador pode editar se canEdit for true
      (isAssigned && demanda.status === 'EMPROGRESSO') // Atribuído pode editar em progresso

    // REMOVER ATRIBUÍDO: Apenas gestor do setor da demanda
    const canRemoveAssigned = (isGestor || hasGlobalManagementRole) && hasActiveAssignment

    // LÓGICA ESPECIAL PARA DEMANDAS MARCADAS COMO ERRADAS:
    // Quando uma demanda está com status "ERRADA", apenas comentar e reenviar devem estar disponíveis
    const isWrongDemand = demanda.status === 'ERRADA'
    
    if (isWrongDemand) {
      return {
        canPull: false,
        canAssign: false,
        canSolve: false,
        canFinalize: false,
        canReturn: false,
        canAccept: false,
        canReject: false,
        canComment: canComment, // Mantém a lógica normal de quem pode comentar
        canAttach: canAttach, // Mantém a lógica normal de quem pode anexar
        canAddObservers: false,
        canDelete: false,
        canMarkWrong: false,
        canResend: canResend, // Mantém a lógica normal de quem pode reenviar
        canUndoDelivery: false,
        canEdit: false,
        canEditTemplateFields: false,
        canRemoveAssigned: false,

        // Informações de contexto
        isCreator,
        isDemandante,
        isAssigned,
        isObserver,
        hasActiveAssignment,
        isGestorDoSetor: isGestor,
        isAdmin,
      }
    }

    return {
      canPull,
      canAssign,
      canSolve,
      canFinalize,
      canReturn,
      canAccept,
      canReject,
      canComment,
      canAttach,
      canAddObservers,
      canDelete,
      canMarkWrong,
      canResend,
      canUndoDelivery,
      canEdit,
      canEditTemplateFields,
      canRemoveAssigned,

      // Informações de contexto
      isCreator,
      isDemandante,
      isAssigned,
      isObserver,
      hasActiveAssignment,
      isGestorDoSetor: isGestor,
      isAdmin,
    }
  }, [demanda, currentUser])
}

export default useDemandPermissions
